"use client";

import React from "react";
import { ArrowRight, Ruler } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ProjectInsightsGrid() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="border-2 border-border rounded-none animate-pulse bg-card/50">
            <div className="h-64 bg-muted/20 border-b-2 border-border" />
            <CardHeader>
              <div className="h-6 bg-muted/30 w-3/4 mb-2" />
              <div className="h-4 bg-muted/20 w-1/2" />
            </CardHeader>
            <CardContent>
              <div className="h-20 bg-muted/20 w-full" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-16 border-2 border-destructive/50 bg-destructive/10 rounded-none">
        <p className="text-destructive font-mono uppercase tracking-widest text-sm">
          System Error: Unable to retrieve project data.
        </p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-24 border-2 border-border border-dashed rounded-none bg-card/30">
        <Ruler className="mx-auto size-12 text-muted-foreground mb-4 opacity-50" />
        <p className="text-muted-foreground font-mono uppercase tracking-widest text-sm">
          No case studies currently available.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="group border-2 border-border rounded-none bg-card hover:border-primary transition-colors duration-300 flex flex-col overflow-hidden">
          {post.featuredImage ? <div className="relative h-64 overflow-hidden border-b-2 border-border">
              <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-100 group-hover:scale-105" />
              <div className="absolute inset-0 bg-background/20 group-hover:bg-transparent transition-colors duration-500" />
            </div> : <div className="relative h-64 bg-muted/5 border-b-2 border-border flex items-center justify-center">
              <Ruler className="size-16 text-muted-foreground opacity-20" />
            </div>}

          <CardHeader className="flex-none pt-8 pb-4">
            <div className="flex flex-wrap gap-2 mb-4">
              {post.categories && post.categories.length > 0 ? post.categories.slice(0, 2).map((cat, idx) => <Badge key={cat.id} data-index={idx} variant="outline" className="rounded-none border-border font-mono uppercase text-[10px] tracking-widest bg-background/50">
                    {cat.name}
                  </Badge>) : <>
                  <Badge variant="outline" className="rounded-none border-border font-mono uppercase text-[10px] tracking-widest bg-background/50">
                    Double Storey Extension
                  </Badge>
                  <Badge variant="outline" className="rounded-none border-border font-mono uppercase text-[10px] tracking-widest bg-background/50">
                    Attic Conversion
                  </Badge>
                </>}
            </div>
            <CardTitle className="text-2xl font-serif tracking-tight leading-tight group-hover:text-primary transition-colors">
              {post.title}
            </CardTitle>
          </CardHeader>

          <CardContent className="flex-grow pb-8">
            <p className="text-muted-foreground line-clamp-3 text-sm leading-relaxed font-sans">
              {post.excerpt ? post.excerpt.replace(/<[^>]*>/g, "") : "Technical challenges met with uncompromising architectural solutions. Establishing new standards for luxury builds and structural integrity in North West London."}
            </p>
          </CardContent>

          <CardFooter className="flex-none p-0 border-t-2 border-border">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="ghost" className="w-full rounded-none hover:bg-primary hover:text-primary-foreground transition-all uppercase tracking-widest font-mono text-xs h-16 flex items-center justify-between px-6">
                <span>View Blog Post</span>
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-2" />
              </Button>
            </Link>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function ProjectInsights() {
  const wp_query_params = {
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="project-insights" className="dark bg-background text-foreground py-24 md:py-32 px-4 md:px-8 lg:px-12 border-y-8 border-primary relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
      backgroundSize: '4rem 4rem'
    }} />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-16 md:mb-24 border-l-8 border-primary pl-6 md:pl-10">
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold uppercase tracking-tighter mb-6 text-foreground">
            Project Insights
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl font-mono tracking-tight leading-relaxed">
            Technical excellence and architectural mastery. Discover why SolidTrust Builders are recognized as the best builders in North West London, specializing in complex home extensions and loft conversions.
          </p>
        </div>

        <PostsProvider wp_query={{
        per_page: 6,
        order: "desc",
        orderby: "date"
      }}>
          <ProjectInsightsGrid />
        </PostsProvider>
      </div>
    </section>;
}